import React, { useLayoutEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SERVICE_PROFILE_LIST } from '../data/serviceProfiles';
import ServiceFlag from './ServiceFlag';
import { CFASTButton } from '../modules/cbtCommon';

const BASE_PREFERENCES = {
  commercial_profile: 'commercial-general',
  candidate_stage: null,
  assessment_date: null,
  training_days_per_week: 4,
  typical_session_minutes: 30,
  preferred_rest_day: null
};

// Shown the first time a user reaches a dashboard without ever having
// chosen a preparation track -- previously the only prompt for this lived
// on "/" (see HomePage's redirect to /onboarding), so a user who bookmarked
// or was linked straight to /dashboard never saw it at all. Self-gating: it
// renders null once the account has a track, so it's safe to mount
// unconditionally alongside either dashboard.
const TrackSelectionModal = () => {
  const { profile, isAdmin, siteSettings, updateTrainingProfile, updatePreparationPreferences } = useAuth();
  const commercialTrackVisible = isAdmin || siteSettings?.commercial_track_enabled === true;

  // profile loads asynchronously and its default shape also lacks
  // service_profile, so without this check the modal would flash open for
  // an already-onboarded user whose profile hasn't finished loading yet.
  const profileKnown = profile && Object.prototype.hasOwnProperty.call(profile, 'service_profile');
  const needsTrackSelection = profileKnown
    && !profile.service_profile
    && !profile.preparation_preferences?.commercial_enabled;

  const [step, setStep] = useState('family');
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    if (!commercialTrackVisible) setStep('service');
  }, [commercialTrackVisible]);

  if (!needsTrackSelection || dismissed) return null;

  const saveMilitary = async (serviceProfileId) => {
    setSaving(true);
    setError('');
    const trainingResult = await updateTrainingProfile({ serviceProfile: serviceProfileId, targetRole: null });
    if (trainingResult.error) {
      setSaving(false);
      setError(trainingResult.error.message || 'Unable to save your training profile.');
      return;
    }
    const prefsResult = await updatePreparationPreferences({
      ...BASE_PREFERENCES,
      military_enabled: true,
      commercial_enabled: false,
      active_family: 'military',
      military_profile: serviceProfileId,
      military_role: null
    });
    setSaving(false);
    if (prefsResult.error) setError(prefsResult.error.message || 'Unable to save your preparation settings.');
  };

  const saveCommercial = async () => {
    setSaving(true);
    setError('');
    const prefsResult = await updatePreparationPreferences({
      ...BASE_PREFERENCES,
      military_enabled: false,
      commercial_enabled: true,
      active_family: 'commercial',
      military_profile: profile?.service_profile || 'general',
      military_role: null
    });
    setSaving(false);
    if (prefsResult.error) setError(prefsResult.error.message || 'Unable to save your preparation settings.');
  };

  return (
    <Dialog.Root open onOpenChange={(next) => !next && setDismissed(true)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-[2px]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[101] w-[calc(100%_-_2rem)] max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-[#334c65] bg-[#0a1524] p-6 text-white shadow-[0_24px_80px_rgba(0,0,0,0.72)] outline-none"
          data-testid="track-selection-modal"
        >
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md border border-[#334c65] bg-[#0e1c30] text-[#8fa5bd] transition-colors hover:border-[#68d9ff] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#68d9ff] disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close track selection"
              disabled={saving}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </Dialog.Close>

          {step === 'family' ? (
            <>
              <Dialog.Title className="text-lg font-semibold text-white">What are you preparing for?</Dialog.Title>
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-[#8fa5bd]">
                Choose a preparation area to unlock your dashboard. You can change this later from Account settings.
              </Dialog.Description>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  data-testid="track-selection-military"
                  onClick={() => setStep('service')}
                  disabled={saving}
                  className="site-panel p-5 text-left transition-colors hover:border-[#68d9ff] disabled:opacity-60"
                >
                  <span className="block text-base font-semibold text-white">Military CBAT</span>
                  <span className="mt-2 block text-xs leading-relaxed text-[#8fa5bd]">RAF, CFAST, ADF and other military aviation aptitude preparation.</span>
                </button>
                {commercialTrackVisible && (
                  <button
                    type="button"
                    data-testid="track-selection-commercial"
                    onClick={() => void saveCommercial()}
                    disabled={saving}
                    className="site-panel p-5 text-left transition-colors hover:border-[#68d9ff] disabled:opacity-60"
                  >
                    <span className="block text-base font-semibold text-white">Commercial Aviation</span>
                    <span className="mt-2 block text-xs leading-relaxed text-[#8fa5bd]">Airline, cadet-pilot and flight-school aptitude preparation.</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <Dialog.Title className="text-lg font-semibold text-white">Which selection system are you preparing for?</Dialog.Title>
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-[#8fa5bd]">
                This sets your military workspace. You can change it later from Account settings.
              </Dialog.Description>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SERVICE_PROFILE_LIST.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`track-selection-service-${item.id}`}
                    onClick={() => void saveMilitary(item.id)}
                    disabled={saving}
                    className="site-panel flex items-start gap-3 p-5 text-left transition-colors hover:border-[#68d9ff] disabled:opacity-60"
                  >
                    <ServiceFlag flag={item.flag} className="mt-1" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold text-white">{item.onboardingLabel || item.label}</span>
                      <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-[#68d9ff]">{item.shortLabel}</span>
                    </span>
                  </button>
                ))}
              </div>
              {commercialTrackVisible && (
                <div className="mt-6">
                  <CFASTButton testid="track-selection-back" onClick={() => setStep('family')} disabled={saving}>Back</CFASTButton>
                </div>
              )}
            </>
          )}
          {error && <p className="mt-4 text-xs text-[#ff9ba4]" role="alert">{error}</p>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TrackSelectionModal;
